import type { TFunction } from "i18next";
import { compact, flatMap, isNil, map, sumBy } from "lodash";
import { formatPrice } from "./price";
import {
	formatVatRate,
	lineAmountCents,
	type VatBreakdownRow,
} from "./sale-lines";

// Helpers shared by the receipt and invoice layouts.
//
// `electron-pos-printer` laid the product table out as an HTML table with 10% /
// 65% / 25% column widths. A thermal printer has no such thing as a percentage:
// it has a fixed number of character cells per line, so the same split is
// resolved to whole characters here.

type Translate = TFunction<"translation", undefined>;

// Printed after every amount instead of "€": the Rust side sends the printer
// Unicode code points rather than its code page, and "€" comes out as "?".
export const PRINTED_CURRENCY = "EUR";

/** The widest amount the columns make room for, e.g. "9999.99 EUR". */
export const PRICE_WIDTH = 11;

/** Character widths for the quantity / name / price columns. */
export const receiptTableWidths = (columns: number) => {
	// Floors so that a 44mm roll still leaves room for a two-digit quantity and a
	// price like "9999.99 EUR"; the name column absorbs whatever is left.
	const quantity = Math.max(3, Math.round(columns * 0.1));
	const price = Math.max(PRICE_WIDTH, Math.round(columns * 0.25));

	return { quantity, price, name: Math.max(1, columns - quantity - price) };
};

/** A right-aligned label/amount line, used for the totals. */
export const receiptTotalLine = (
	text: string,
	columns: number,
): ReceiptLine => ({
	kind: "columns",
	cols: [{ text, width: columns, align: "right" }],
});

/** A label on the left and an amount on the right, e.g. a payment. */
export const receiptAmountLine = (
	label: string,
	amount: string,
	columns: number,
): ReceiptLine => {
	const { price } = receiptTableWidths(columns);

	return {
		kind: "columns",
		cols: [
			{ text: label, width: columns - price },
			{ text: amount, width: price, align: "right" },
		],
	};
};

/**
 * Splits text into lines of at most `columns` characters, between words. The
 * printer would otherwise wrap a long legal mention mid-word, and the Rust
 * side drops embedded newlines rather than let them through.
 */
export const wrapText = (text: string, columns: number): string[] => {
	const lines: string[] = [];
	let line = "";

	for (const word of text.split(/\s+/).filter(Boolean)) {
		if (!line) {
			line = word;
		} else if (line.length + 1 + word.length <= columns) {
			line = `${line} ${word}`;
		} else {
			lines.push(line);
			line = word;
		}

		// A word longer than a line is cut, rather than lost.
		while (line.length > columns) {
			lines.push(line.slice(0, columns));
			line = line.slice(columns);
		}
	}

	return line ? [...lines, line] : lines;
};

/** Text wrapped to the paper width, one printed line per wrapped line. */
export const receiptText = (
	text: string,
	columns: number,
	options: { align?: "left" | "center" | "right"; bold?: boolean } = {},
): ReceiptLine[] =>
	map(
		wrapText(text, columns),
		(line): ReceiptLine => ({ kind: "text", text: line, ...options }),
	);

/** "73282932000074" as it is usually printed: "732 829 320 00074". */
export const formatSiret = (siret: string) =>
	siret.replace(/^(\d{3})(\d{3})(\d{3})(\d*)$/, "$1 $2 $3 $4").trim();

/** Who sold, as receipts and invoices state it. */
export interface SellerIdentity {
	/** The store's name, printed large at the top. */
	name: string;
	/** The raison sociale, printed under the name when it differs. */
	legalName?: string | null;
	legalForm?: string | null;
	shareCapitalCents?: number | null;
	addressLines: string[];
	/** Phone, email, website: whatever the store gave. */
	contactLines?: string[];
	siret?: string | null;
	vatNumber?: string | null;
	rcs?: string | null;
}

/** "SAS au capital de 10000.00 EUR", or as much of it as is known. */
const legalFormLine = (seller: SellerIdentity, t: Translate) => {
	const capital = isNil(seller.shareCapitalCents)
		? undefined
		: formatPrice(seller.shareCapitalCents, "EUR", PRINTED_CURRENCY);

	if (seller.legalForm && capital) {
		return t("print.identity.legalFormWithCapital", {
			legalForm: seller.legalForm,
			capital,
		});
	}
	if (capital) {
		return t("print.identity.shareCapital", { capital });
	}
	return seller.legalForm ?? undefined;
};

/**
 * The seller's header. Every part of the legal identity is optional, since a
 * store set up before it existed has none: missing parts are left out rather
 * than printed blank or as "undefined".
 */
export const sellerLines = (
	seller: SellerIdentity,
	t: Translate,
	columns: number,
): ReceiptLine[] => [
	{ kind: "text", text: seller.name, align: "center", bold: true, size: 2 },
	...flatMap(
		compact([
			seller.legalName !== seller.name ? seller.legalName : undefined,
			legalFormLine(seller, t),
			...seller.addressLines,
			...(seller.contactLines ?? []),
			seller.siret &&
				t("print.identity.siret", { siret: formatSiret(seller.siret) }),
			seller.vatNumber &&
				t("print.identity.vatNumber", { vatNumber: seller.vatNumber }),
			seller.rcs,
		]),
		(text) => receiptText(text, columns, { align: "center" }),
	),
];

/** Character widths for the rate / tax-free / VAT columns. */
export const vatTableWidths = (columns: number) => {
	const rate = 6;
	const amount = Math.floor((columns - rate) / 2);

	return { rate, taxFree: columns - rate - amount, vat: amount };
};

/**
 * The VAT a sale declared, one row per rate, as the receipt and the invoice
 * both have to show it. Read from the lines' stored amounts, never re-derived.
 */
export const vatTable = (
	rows: VatBreakdownRow[],
	currency: string,
	t: Translate,
	columns: number,
): ReceiptLine[] => {
	const width = vatTableWidths(columns);
	const row = (rate: string, taxFree: string, vat: string): ReceiptLine => ({
		kind: "columns",
		cols: [
			{ text: rate, width: width.rate },
			{ text: taxFree, width: width.taxFree, align: "right" },
			{ text: vat, width: width.vat, align: "right" },
		],
	});
	const price = (cents: number) =>
		formatPrice(cents, currency, PRINTED_CURRENCY);

	return [
		row(
			t("print.vat.rate"),
			t("print.vat.taxFreeAmount"),
			t("print.vat.vatAmount"),
		),
		...map(rows, (vatRow) =>
			row(
				formatVatRate(vatRow.ratePercentCents),
				price(vatRow.taxFreeAmountCents),
				price(vatRow.vatAmountCents),
			),
		),
	];
};

/** What the sale's payments were made with, e.g. "Card, Cash". */
export const paymentMethods = (salePayments: SalePayment[]) =>
	compact(map(salePayments, (payment) => payment.paymentType?.name)).join(", ");

/** What the lines came to at the till, less what the sale was charged. */
export const saleDiscountCents = (sale: Sale, saleProducts: SaleProduct[]) =>
	Math.max(0, sumBy(saleProducts, lineAmountCents) - sale.totalAmountCents);
